/// <amd-module name="Test/TestCaseResults/Redux/TestCaseResultsSelector" />
import * as Reselect from "Reselect";
import * as Constants from "Test/TestCaseResults/Constants";
import * as Utils from "Test/TestCaseResults/Utils";
import { IListBoxItemData } from "Test/TestCaseResultsFilter/TestCaseResultsFilterComponent.Types";
import * as Contracts from "TestPlanCommon/Contracts/TestPlan";
import { ObservableArray } from "VSS/Core/Observable";
import { IListBoxItem } from "VSSUI/ListBox";
import { IFilterState } from "VSSUI/Utilities/Filter";

import { ITestCaseResultsAwareState } from "./TestCaseResultsContracts";

export const isAnyFilterValueSet = (filterState: IFilterState | undefined) => {
    let isFilterValueSet = false;

    const filterItemKeys = [
        Constants.OutcomeFilterKey,
        Constants.ConfigurationFilterKey,
        Constants.TesterFilterKey,
        Constants.PlanFilterKey,
        Constants.SuiteFilterKey
    ];

    // Check if any of the filter item keys are set
    if (filterState) {
        for (const key of filterItemKeys) {
            const filterItemState = filterState[key];
            if (filterItemState && filterItemState.value && filterItemState.value.length) {
                isFilterValueSet = true;
                break;
            }
        }
    }

    return isFilterValueSet;
};

export const getResultItemsFromState = (state: ITestCaseResultsAwareState): Contracts.TestCaseAssociatedResult[] | undefined => {
    return state.testCaseResultsState.testCaseResults;
};

/**
 * getUniqueFilterValuesToRender: This selector loops through all the items and returns an object containing unique values for each of the filter
 */
export const getUniqueFilterValuesToRender = Reselect.createSelector(
    getResultItemsFromState,
    (allResultItems: Contracts.TestCaseAssociatedResult[]) => {
        const outcomeValues: ObservableArray<IListBoxItem<IListBoxItemData>> = new ObservableArray<IListBoxItem<IListBoxItemData>>();
        const testerValues: ObservableArray<IListBoxItem<IListBoxItemData>> = new ObservableArray<IListBoxItem<IListBoxItemData>>();
        const configurationValues: ObservableArray<IListBoxItem<IListBoxItemData>> = new ObservableArray<IListBoxItem<IListBoxItemData>>();
        const planValues: ObservableArray<IListBoxItem<IListBoxItemData>> = new ObservableArray<IListBoxItem<IListBoxItemData>>();
        const suiteValues: ObservableArray<IListBoxItem<IListBoxItemData>> = new ObservableArray<IListBoxItem<IListBoxItemData>>();

        if (allResultItems && allResultItems.length > 0) {
            const outcomeMap: Map<string, number> = new Map<string, number>();
            const testerMap: Map<string, number> = new Map<string, number>();
            const configurationMap: Map<string, number> = new Map<string, number>();
            const planMap: Map<string, number> = new Map<string, number>();
            const suiteMap: Map<string, number> = new Map<string, number>();

            for (const resultItem of allResultItems) {
                const outcomeDisplayString = Utils.getOutcomeString(resultItem.outcome);
                if (!outcomeMap.has(outcomeDisplayString)) {
                    outcomeMap.set(outcomeDisplayString, resultItem.pointId);

                    const item: IListBoxItem<IListBoxItemData> = {
                        id: outcomeDisplayString,
                        text: outcomeDisplayString,
                        data: {
                            key: outcomeDisplayString,
                            value: resultItem.outcome
                        }
                    };
                    outcomeValues.push(item);
                }

                const testerDisplayName = Utils.getDisplayName(resultItem.tester);
                if (!testerMap.has(testerDisplayName)) {
                    testerMap.set(testerDisplayName, resultItem.pointId);

                    const item: IListBoxItem<IListBoxItemData> = {
                        id: testerDisplayName,
                        text: testerDisplayName,
                        data: {
                            key: testerDisplayName,
                            value: resultItem.tester
                        }
                    };
                    testerValues.push(item);
                }

                if (!configurationMap.has(resultItem.configuration.name)) {
                    if (resultItem.configuration.name && resultItem.configuration.name.length > 0) {
                        configurationMap.set(resultItem.configuration.name, resultItem.pointId);

                        const item: IListBoxItem<IListBoxItemData> = {
                            id: resultItem.configuration.name,
                            text: resultItem.configuration.name,
                            data: {
                                key: resultItem.configuration.name,
                                value: resultItem.configuration
                            }
                        };
                        configurationValues.push(item);
                    }
                }

                if (!planMap.has(resultItem.plan.name)) {
                    planMap.set(resultItem.plan.name, resultItem.pointId);

                    const item: IListBoxItem<IListBoxItemData> = {
                        id: resultItem.plan.name,
                        text: resultItem.plan.name,
                        data: {
                            key: resultItem.plan.name,
                            value: resultItem.plan
                        }
                    };
                    planValues.push(item);
                }

                if (!suiteMap.has(resultItem.suite.name)) {
                    if (resultItem.suite.name && resultItem.suite.name.length > 0) {
                        suiteMap.set(resultItem.suite.name, resultItem.pointId);

                        const item: IListBoxItem<IListBoxItemData> = {
                            id: resultItem.suite.name,
                            text: resultItem.suite.name,
                            data: {
                                key: resultItem.suite.name,
                                value: resultItem.suite
                            }
                        };
                        suiteValues.push(item);
                    }
                }
            }
        }

        const wrapperObject: { [key: string]: ObservableArray<IListBoxItem<IListBoxItemData>> } = {};
        wrapperObject[Constants.OutcomeFilterKey] = outcomeValues;
        wrapperObject[Constants.TesterFilterKey] = testerValues;
        wrapperObject[Constants.ConfigurationFilterKey] = configurationValues;
        wrapperObject[Constants.PlanFilterKey] = planValues;
        wrapperObject[Constants.SuiteFilterKey] = suiteValues;

        return wrapperObject;
    }
);

export const getResultsToRender = (state: ITestCaseResultsAwareState): Contracts.TestCaseAssociatedResult[] | undefined => {
    if (!state.testCaseResultsFilterState.filteredResultIds) {
        return state.testCaseResultsState.testCaseResults;
    }

    const filteredResults: Contracts.TestCaseAssociatedResult[] = [];
    for (const result of state.testCaseResultsState.testCaseResults) {
        for (const filteredResult of state.testCaseResultsFilterState.filteredResultIds) {
            if (result.runId == filteredResult.runId && result.resultId == filteredResult.resultId) {
                filteredResults.push(result);
            }
        }
    }
    return filteredResults;
};
